import { useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

/**
 * Tracks user activity. Sends heartbeat every 30s.
 * If session was force-ended by another login, immediately logs out.
 */
export function useSessionManager() {
  const lastActivity = useRef(Date.now());
  const timeoutSeconds = useRef(3600);
  const heartbeatRef = useRef(null);
  const checkRef = useRef(null);

  const forceLogout = useCallback(() => {
    // H3 FIX: Removed localStorage - cookies are cleared server-side
    window.location.href = '/login?reason=session_ended';
  }, []);

  const recordActivity = useCallback(() => {
    lastActivity.current = Date.now();
  }, []);

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(e => window.addEventListener(e, recordActivity, { passive: true }));

    // Heartbeat — every 30s (validates session is still active)
    heartbeatRef.current = setInterval(async () => {
      if (document.hidden) return;
      try {
        const r = await api.post('/api/auth/heartbeat');
        if (r.data?.session_timeout_seconds) {
          timeoutSeconds.current = r.data.session_timeout_seconds;
        }
      } catch (err) {
        // 401 = session no longer valid (force-kicked or expired)
        if (err?.response?.status === 401) {
          forceLogout();
        }
      }
    }, 30000);

    // Idle timeout check — every 15s
    checkRef.current = setInterval(() => {
      const idle = (Date.now() - lastActivity.current) / 1000;
      if (idle > timeoutSeconds.current) {
        api.post('/api/auth/logout').catch(() => {});
        forceLogout();
      }
    }, 15000);

    return () => {
      events.forEach(e => window.removeEventListener(e, recordActivity));
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (checkRef.current) clearInterval(checkRef.current);
    };
  }, [recordActivity, forceLogout]);
}
